import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorPage } from '@screentone/addon-auth-wrapper';
import { useQueryClient } from '@tanstack/react-query';

import { ResponsiveLoader } from 'components';
import { AllessehContentUpdateProvider } from 'contexts/allesseh-content-update/AllessehContentUpdateContext';
import { ContentIdTrackingProvider } from 'contexts/content-id-tracking/ContentIdTrackingContext';
import { DataModelProvider } from 'contexts/datamodel/DataModelContext';
import { DragAndDropProvider } from 'contexts/drag-and-drop/DragAndDropContext';
import { PagePublishProvider } from 'contexts/page-publish-dto/PagePublishContext';
import { PageTypeSettingsProvider } from 'contexts/page-type-settings/PageTypeSettingsContext';
import { useTreatmentTypeSettings } from 'contexts/structure-edit/useTreatmentTypeSettings';
import { SummarianProvider } from 'contexts/summarian/SummarianContext';
import { TrashProvider } from 'contexts/trash/TrashContext';
import {
  ArtifactType,
  Layout,
  PageDto,
  PageType,
  useGetArtifactSettingQuery,
  usePageDtoByAllessehIdQuery
} from 'data/generated/graphql';
import { ScratchpadProvider } from 'features/section-page-edit/components/scratch-pad/scratchpad-context';
import { SectionPageEdit } from 'features/section-page-edit/SectionPageEdit';
import { useConvertedProperty } from 'hooks';
import { ROLE_TYPE } from 'hooks/roles/types';
import useRequireRoles from 'hooks/roles/useRequireRoles';
import { useCommandFSearch } from 'hooks/useCommandFSearch';
import { reportError } from 'utils/errorBoundary';
import { determineLayoutToFetch } from 'utils/layoutUtils';
import { getCollectionIdPrefix } from 'utils/text';
import { getNewPageId } from './constants';
import { buildNewPageDataFromLayout, buildRootFromStructure, preparePageData } from './helpers';

import { Structure } from 'utils/structure.utils';

const NewSectionPage = () => {
  useRequireRoles(ROLE_TYPE.ADMIN, ROLE_TYPE.SUPER);

  const queryClient = useQueryClient();
  const currentProperty = useConvertedProperty();
  const [searchParams] = useSearchParams();
  const ttCatalog = useTreatmentTypeSettings(currentProperty ?? '');

  const layoutIdParam = searchParams.get('layoutId');
  const allessehIdParam = searchParams.get('allessehId');
  const pageTitleParam = searchParams.get('pageTitle');

  const newPageId = useMemo(() => (currentProperty ? getNewPageId(currentProperty) : undefined), [currentProperty]);

  // Layout-selector path: layoutId is present but no allessehId (creating a fresh page from a layout template).
  // When allessehId is also present (Clone Layout), we fall through to the legacy path so that the source
  // page's root attributes, metadata, and shouldCleanContent behaviour are all preserved unchanged.
  const isLayoutSelectorPath = !!layoutIdParam && !allessehIdParam;

  // Layout-artifact path: fetch the layout for its structure (or groups for old artifacts). Baseline DTO not needed.
  // layoutIdParam is a bare layout ID (e.g. "style"); compound artifactId is "{pageType}_{id}" (e.g. "SECTION_style").
  const layoutArtifactId = layoutIdParam ? `${PageType.Section}_${layoutIdParam}` : '';
  const { data: layoutArtifactResult, error: layoutArtifactError } = useGetArtifactSettingQuery(
    { publicationKey: currentProperty ?? '', artifactId: layoutArtifactId, artifactType: ArtifactType.Layout },
    { enabled: !!currentProperty && isLayoutSelectorPath }
  );

  // Legacy path (no layoutIdParam, or Clone Layout which also provides allessehId): fetch baseline, optionally clone a template DTO.
  const { data: baselineResult, error: baselineError } = usePageDtoByAllessehIdQuery(
    { allessehId: newPageId ?? '', publicationKey: currentProperty ?? '' },
    { enabled: !!currentProperty && !isLayoutSelectorPath }
  );

  const layoutToFetch = useMemo(() => {
    if (isLayoutSelectorPath) return newPageId ?? '';
    if (allessehIdParam) return allessehIdParam;
    const baseline = baselineResult?.pageDTOByAllessehId as PageDto | undefined;
    return determineLayoutToFetch({
      layoutIdParam: null,
      baselinePageRoot: baseline?.root,
      baselineAllessehId: newPageId ?? '',
      getCollectionIdPrefix,
      currentProperty: currentProperty ?? ''
    });
  }, [isLayoutSelectorPath, allessehIdParam, baselineResult, currentProperty, newPageId]);

  const shouldClone = !isLayoutSelectorPath && layoutToFetch !== newPageId;

  const { data: clonedResult, error: cloneError } = usePageDtoByAllessehIdQuery(
    { allessehId: layoutToFetch, publicationKey: currentProperty ?? '' },
    { enabled: !!currentProperty && shouldClone }
  );

  // useMemo so that re-renders triggered by ttCatalog loading don't produce a new metadata object
  // reference — DataModelContext resets its internal metadata state whenever initialMetadata changes
  // identity, which would wipe out any title the user typed.
  const preparedPageData = useMemo(() => {
    if (isLayoutSelectorPath && currentProperty) {
      return buildNewPageDataFromLayout(currentProperty, layoutIdParam);
    }
    const baselinePageDTO = baselineResult?.pageDTOByAllessehId as PageDto | undefined;
    if (!baselinePageDTO) return null;
    return preparePageData({
      shouldClone,
      baselinePageDTO,
      clonedResult,
      pageTitleParam,
      layoutToFetch,
      currentProperty
    });
  }, [
    isLayoutSelectorPath,
    layoutIdParam,
    currentProperty,
    shouldClone,
    baselineResult,
    clonedResult,
    pageTitleParam,
    layoutToFetch
  ]);

  useCommandFSearch();

  // Read structure directly from the layout artifact (new artifacts written by the layout editor).
  const layoutBody =
    isLayoutSelectorPath && layoutArtifactResult?.getArtifactSetting
      ? (layoutArtifactResult.getArtifactSetting.body as (Layout & { structure?: Structure }) | null | undefined)
      : undefined;
  const layoutStructure = layoutBody?.structure;

  // Memoized so that a parent/query rerender after editing does not produce a new root object
  // reference, which would cause DataModelContext to reset its working root and change flags.
  const root = useMemo(() => {
    const preparedRoot = preparedPageData?.root;
    if (!preparedRoot) return undefined;
    if (isLayoutSelectorPath && layoutStructure) {
      // Wait for the catalog before building: an empty catalog produces a structurally incomplete
      // root that DataModelContext would treat as a separate identity once the real catalog arrives,
      // wiping any edits made in the interim.
      if (ttCatalog.length === 0) return undefined;
      return buildRootFromStructure(preparedRoot, layoutStructure, layoutIdParam, ttCatalog);
    }
    return preparedRoot;
  }, [preparedPageData, isLayoutSelectorPath, layoutIdParam, layoutStructure, ttCatalog]);

  const queryError = (!isLayoutSelectorPath && baselineError) || cloneError || layoutArtifactError;
  if (queryError) {
    reportError('Error fetching section page data: ', queryError as Error);
    const ERROR_CODE = queryError instanceof Error && queryError.message === 'Failed to fetch' ? '503' : '404';
    return <ErrorPage type={ERROR_CODE} />;
  }

  const hasRequiredData = isLayoutSelectorPath
    ? !!layoutArtifactResult?.getArtifactSetting
    : !!baselineResult && (!shouldClone || !!clonedResult);
  if (!hasRequiredData || !preparedPageData || !root) {
    return <ResponsiveLoader />;
  }

  const { metadata, articlePromotionLog, scratchpad, pageType, queryKey } = preparedPageData;

  const activeStructure: Structure | undefined = (() => {
    if (isLayoutSelectorPath) {
      if (layoutStructure) return layoutStructure;
    }
    const baselinePageDTO = baselineResult?.pageDTOByAllessehId as PageDto | undefined;
    const fromDTO = shouldClone
      ? (clonedResult?.pageDTOByAllessehId as PageDto | undefined)?.structure
      : baselinePageDTO?.structure;
    return fromDTO ?? undefined;
  })();

  return (
    <AllessehContentUpdateProvider>
      <SummarianProvider>
        <DataModelProvider
          type="PageDTO"
          root={root}
          metadata={metadata}
          articlePromotionLog={articlePromotionLog}
          shouldCleanContent={!isLayoutSelectorPath && shouldClone}
          structure={activeStructure}
        >
          <ScratchpadProvider scratchpad={scratchpad}>
            <ContentIdTrackingProvider>
              <PageTypeSettingsProvider pageType={pageType}>
                <PagePublishProvider
                  setData={(data) => {
                    queryClient.setQueryData(queryKey, data);
                  }}
                >
                  <TrashProvider>
                    <DragAndDropProvider>
                      <SectionPageEdit />
                    </DragAndDropProvider>
                  </TrashProvider>
                </PagePublishProvider>
              </PageTypeSettingsProvider>
            </ContentIdTrackingProvider>
          </ScratchpadProvider>
        </DataModelProvider>
      </SummarianProvider>
    </AllessehContentUpdateProvider>
  );
};

export default NewSectionPage;
